"use client";

import type { KeyboardEvent } from "react";
import { useState } from "react";
import { ArrowRight, CalendarDays, Check, ChevronDown } from "lucide-react";
import { useFormatter, useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import {
  coalesceCourseMoney,
  formatCourseMoneyEnIn,
  getLocalizedField,
  type PricingPlan,
} from "../api";
import type { Locale } from "@/lib/i18n";
import { CourseEnrollCta } from "./CourseEnrollCta";
import { CohortWaitlistForm } from "./CohortWaitlistForm";
import { SprintCountdown } from "./SprintCountdown";
import {
  resolvePlanCardOwnedDisplay,
  type OwnedState,
} from "../lib/resolve-course-plan-owned-state";
import {
  formatTierUpgradeCtaLabel,
  type CoursePlanPricingUiModel,
} from "../lib/resolve-course-plan-pricing-ui-state";
import { getOwnershipCopy } from "../lib/ownership-copy";
import { getSeatIndicator } from "../lib/course-pricing-helpers";
import { cohortStatus } from "../lib/cohort-line";

export function CoursePricingTierPlanCard({
  plan,
  locale,
  ownedState,
  isPopular,
  recommendedPlanId,
  courseId,
  slug,
  firstLessonId,
  sortedPlans,
  isAuthenticated,
  isOwned,
  pricingUiModel,
  ownedCourse,
  eligibilityLocked,
  eligibilityLockedReason,
  showSprintCountdown,
  isSelected,
  onSelectPlan,
}: {
  plan: PricingPlan;
  locale: Locale;
  ownedState: OwnedState;
  isPopular: boolean;
  /** Highlight suggested tier without implying user selection */
  recommendedPlanId?: string | null;
  courseId: string;
  slug: string;
  firstLessonId?: string | null;
  sortedPlans: PricingPlan[];
  isAuthenticated: boolean;
  isOwned: boolean;
  pricingUiModel: CoursePlanPricingUiModel;
  ownedCourse: { pricingPlanId?: string } | undefined;
  eligibilityLocked: boolean;
  eligibilityLockedReason: string;
  showSprintCountdown: boolean;
  /** When set, card body is selectable for tier sync */
  isSelected?: boolean;
  onSelectPlan?: (planId: string) => void;
}) {
  const t = useTranslations("courseDetail");
  const format = useFormatter();
  const [featuresExpanded, setFeaturesExpanded] = useState(false);
  const INITIAL_FEATURES = 6;
  const badge = getLocalizedField(plan.badge_en, plan.badge_bn, locale);
  const name =
    getLocalizedField(plan.name_en, plan.name_bn, locale) ?? plan.name_en;
  const description = getLocalizedField(
    plan.description_en,
    plan.description_bn,
    locale,
  );
  const accessSummary = getLocalizedField(
    plan.accessSummary_en,
    plan.accessSummary_bn,
    locale,
  );
  const listAmount = coalesceCourseMoney(plan.price.list);
  const saleAmount = plan.price.sale;
  const hasSale =
    !plan.price.isFree &&
    saleAmount !== undefined &&
    Number.isFinite(saleAmount) &&
    saleAmount < listAmount;
  const currency = plan.price.currency === "BDT" ? "৳" : "$";
  // `GR-CRS01`: the USD pair a non-Bangladesh account pays by card.
  const usdList = plan.priceUsd;
  const usdSale =
    usdList !== undefined && plan.salePriceUsd !== undefined && plan.salePriceUsd < usdList ? plan.salePriceUsd : undefined;
  const languageLabel =
    plan.language === "bn" ? (locale === "bn" ? "বাংলা video" : "Bangla video") : plan.language === "en" ? (locale === "bn" ? "English video" : "English video") : null;
  const seatIndicator = getSeatIndicator(plan, {
    full: t("seats.full"),
    limited: t("seats.limited"),
    onlyLeft: (count) => t("seats.onlyLeft", { count }),
  });

  const display = resolvePlanCardOwnedDisplay(ownedState);
  const copy = getOwnershipCopy(locale);
  const ownedBadgeLabel =
    display.badge === "mine"
      ? copy.ownedPillLabel
      : display.badge === "included"
        ? copy.legacyPillLabel
        : null;

  const effectiveOwnedPlan =
    isAuthenticated && isOwned ? pricingUiModel.effectiveOwnedPlanForCopy : null;

  const isUpgradeFromOwned =
    ownedState.kind === "owned-other" && ownedState.upgradeable;
  const upgradeToPlanId = isUpgradeFromOwned ? plan._id : undefined;
  const upgradeLabel = isUpgradeFromOwned
    ? formatTierUpgradeCtaLabel(plan, pricingUiModel, {
        upgradePay: (amount) => t("planOwnership.upgradePay", { amount }),
        upgradeFree: () => t("planOwnership.upgradeFree"),
      })
    : undefined;

  const nominalLegacyPlan = pricingUiModel.nominalLegacyPlan;
  const ownedTierDisplayName =
    effectiveOwnedPlan &&
    (getLocalizedField(
      effectiveOwnedPlan.name_en,
      effectiveOwnedPlan.name_bn,
      locale,
    ) ??
      effectiveOwnedPlan.name_en);
  const legacyNominalName =
    nominalLegacyPlan &&
    (getLocalizedField(
      nominalLegacyPlan.name_en,
      nominalLegacyPlan.name_bn,
      locale,
    ) ??
      nominalLegacyPlan.name_en);

  const enrolledPeerNotice =
    isAuthenticated && isOwned
      ? ownedState.kind === "owned-legacy-peer" && legacyNominalName
        ? t("planOwnership.enrolledInPlanChip", { planName: legacyNominalName })
        : ownedState.kind === "owned-other" && !ownedState.upgradeable
          ? t("planOwnership.coveredByPlan", {
              planName:
                ownedTierDisplayName ?? t("planOwnership.yourPlanShort"),
            })
          : undefined
      : undefined;

  // `GR-TRU01`: "Next cohort" only while the date is ahead, as on the hero
  // (`docs/11` §7.4: a date that has passed is never "Next").
  let cohortLine: string | null = null;
  if (plan.cohortStartAt && cohortStatus(plan.cohortStartAt)?.kind === "upcoming") {
    cohortLine = t("nextCohort", {
      date: format.dateTime(new Date(plan.cohortStartAt), { dateStyle: "long", timeZone: "Asia/Dhaka" }),
    });
  }

  const selectable = !!onSelectPlan;
  const isRecommendedHint =
    !!recommendedPlanId &&
    recommendedPlanId === plan._id &&
    !isSelected;

  const handleSelectKeyDown = (e: KeyboardEvent) => {
    if (!onSelectPlan) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelectPlan(plan._id);
    }
  };

  const body = (
    <>
      <div className="mb-3 flex min-h-[1.5rem] flex-wrap gap-1.5">
        {badge && (
          <span
            className={cn(
              "inline-flex max-w-full items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
              isPopular
                ? "bg-primary text-white shadow-[0_6px_18px_rgba(233,77,29,0.18)]"
                : "bg-primary text-white",
            )}
            aria-label={t("mostPopularBadgeAria")}
          >
            {badge}
          </span>
        )}
        {ownedBadgeLabel && (
          <span className="inline-flex max-w-full items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
            {ownedBadgeLabel}
          </span>
        )}
        {seatIndicator && (
          <span
            className={cn(
              "inline-flex max-w-full items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
              seatIndicator.warm
                ? "bg-orange-100 text-orange-800"
                : "bg-neutral-100 text-neutral-700",
            )}
          >
            {seatIndicator.label}
          </span>
        )}
      </div>

      <h3
        className="mb-1 pr-1 text-[15px] font-semibold leading-snug text-neutral-900"
        id={`tier-title-${plan._id}`}
      >
        {name}
      </h3>

      {description ? (
        <p className="mb-2 line-clamp-2 text-[13px] leading-snug text-neutral-500">
          {description}
        </p>
      ) : null}

      {languageLabel ? (
        <span className="mb-2 inline-flex w-fit items-center rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[11px] font-semibold text-neutral-700">
          {languageLabel}
        </span>
      ) : null}

      <div className="mb-2 flex items-baseline gap-2">
        {plan.price.isFree ? (
          <span className="text-[22px] font-bold text-emerald-600">Free</span>
        ) : (
          <>
            {hasSale && (
              <span className="text-[22px] font-bold text-neutral-950">
                {currency}
                {formatCourseMoneyEnIn(saleAmount)}
              </span>
            )}
            <span
              className={cn(
                hasSale
                  ? "text-[15px] text-neutral-400 line-through"
                  : "text-[22px] font-bold text-neutral-950",
              )}
            >
              {currency}
              {formatCourseMoneyEnIn(listAmount)}
            </span>
          </>
        )}
      </div>

      {!plan.price.isFree && usdList !== undefined ? (
        <p className="mb-2 text-[12px] text-neutral-500">
          {usdSale !== undefined ? (
            <>
              <span className="font-semibold text-neutral-800">${formatCourseMoneyEnIn(usdSale)}</span>{" "}
              <span className="line-through">${formatCourseMoneyEnIn(usdList)}</span>
            </>
          ) : (
            <span className="font-semibold text-neutral-800">${formatCourseMoneyEnIn(usdList)}</span>
          )}{" "}
          {locale === "bn" ? "বাংলাদেশের বাইরে, card-এ" : "outside Bangladesh, by card"}
        </p>
      ) : null}

      {accessSummary && (
        <p className="mb-2 line-clamp-2 text-[13px] leading-snug text-neutral-500">
          {accessSummary}
        </p>
      )}

      {cohortLine && (
        <p className="mb-2 flex items-center gap-1.5 text-[12px] font-medium text-neutral-600">
          <CalendarDays
            className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400"
            aria-hidden
          />
          {cohortLine}
        </p>
      )}

      {showSprintCountdown && plan.sprintEndsAt && (
        <SprintCountdown
          endsAt={plan.sprintEndsAt}
          label={t("sprintCountdown.label")}
          size="compact"
          className="mb-3"
        />
      )}

      {plan.features.length > 0 && (
        <>
          <ul className="mb-2 flex-1 space-y-1.5" role="list">
            {(featuresExpanded
              ? plan.features
              : plan.features.slice(0, INITIAL_FEATURES)
            ).map((feature, i) => {
              const text =
                getLocalizedField(feature.text_en, feature.text_bn, locale) ??
                feature.text_en;
              return (
                <li key={i} className="flex items-start gap-2">
                  <Check
                    className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary"
                    aria-hidden
                  />
                  <span className="text-[13px] leading-snug text-neutral-600">{text}</span>
                </li>
              );
            })}
          </ul>
          {plan.features.length > INITIAL_FEATURES ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setFeaturesExpanded((v) => !v);
              }}
              className="mb-4 flex w-full items-center justify-center gap-1 rounded-lg py-1.5 text-[12px] font-semibold text-primary hover:bg-primary/[0.06]"
            >
              {featuresExpanded
                ? t("planCardShowFewerFeatures")
                : t("planCardShowAllFeatures", { count: plan.features.length })}
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 transition-transform duration-200",
                  featuresExpanded && "rotate-180",
                )}
                aria-hidden
              />
            </button>
          ) : null}
        </>
      )}

      {isUpgradeFromOwned && (
        <p className="mb-2 flex items-center gap-1 text-[12px] font-medium text-primary">
          {copy.upgradePrefix} {name}
          <ArrowRight aria-hidden className="h-3.5 w-3.5" />
        </p>
      )}
    </>
  );

  return (
    <article
      className={cn(
        "relative flex h-full min-h-0 flex-col rounded-2xl border bg-white p-6 transition-all duration-150",
        "border-neutral-200 shadow-[0_8px_30px_rgba(0,0,0,0.05)]",
        isPopular && "z-[1] border-primary ring-1 ring-primary/25",
        isRecommendedHint && "border-primary/30 ring-1 ring-primary/15",
        display.highlightCard && "border-emerald-300 bg-emerald-50/40",
        selectable &&
          isSelected &&
          "ring-2 ring-primary ring-offset-2 ring-offset-white",
      )}
      aria-labelledby={`tier-title-${plan._id}`}
    >
      {selectable ? (
        <div
          role="button"
          tabIndex={0}
          aria-pressed={isSelected ?? false}
          aria-label={t("tierSelectAria", { planName: name })}
          className={cn(
            "flex min-h-0 flex-1 flex-col rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
            "cursor-pointer",
          )}
          onClick={() => onSelectPlan(plan._id)}
          onKeyDown={handleSelectKeyDown}
        >
          {body}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">{body}</div>
      )}

      <div
        className="mt-auto border-t border-neutral-100 pt-4"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {seatIndicator?.full && display.badge === null ? (
          <CohortWaitlistForm
            courseSlug={slug}
            planSlug={plan.slug ?? plan._id}
          />
        ) : (
          <CourseEnrollCta
            courseId={courseId}
            slug={slug}
            price={plan.price}
            pricingPlanId={plan._id}
            enrollLabel={name ? t("enrollInPlan", { planName: name }) : undefined}
            upgradeToPlanId={upgradeToPlanId}
            upgradeLabel={upgradeLabel}
            enrolledPeerNotice={enrolledPeerNotice}
            lockedReason={eligibilityLocked ? eligibilityLockedReason : undefined}
            lockedLabel={eligibilityLocked ? t("eligibilityTierLocked") : undefined}
            locale={locale}
            firstLessonId={firstLessonId}
            className="w-full"
          />
        )}
      </div>
    </article>
  );
}
